import { css, useTheme } from '@emotion/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useUIConfigStore } from 'common-state';
import { globals, ItemCard } from 'common-ui';
import { ListView } from './ListView';
import { getRecommendClient } from '../../utils/recommendClient';

const RelatedItem = React.memo(({ item, showRating }: { item: any; showRating: boolean }) => (
  <ItemCard product={item} showRating={showRating ?? true} showImageZoom={true} />
));

const HeaderComponent = (props: any) => {
  if (!props.recommendations || props.recommendations.length === 0) {
    return <span></span>;
  }
  return <h3 className="text-center related-items-heading">You Might Like</h3>;
};

const ItemComponent = (props: any) => <RelatedItem {...props} showRating={true} />;

export const RelatedItems = ({
  currentObjectID,
  targetRef = null,
  fallbackParameters = null,
}: {
  currentObjectID: string;
  targetRef?: any;
  fallbackParameters?: any;
}) => {
  const indexName = `${process.env.NEXT_PUBLIC_INSTANT_SEARCH_INDEX_NAME}`;
  const [recommendations, setRecommendations] = useState<any[]>([]);

  const theme = useTheme();
  const cmsCarousel = theme.widgets.CmsCarousel ?? {};
  const renderListView = useCallback((props: any) => <ListView {...props} cmsCarousel={cmsCarousel} />, [cmsCarousel]);

  const related = css`
    ${theme.widgets.Product?.related}
  `;

  const currentState = useUIConfigStore(state => state.selectedState);

  const queryFilters =
    globals.country === 'us'
      ? `inStock:true AND (threeTierStates:${currentState} OR wineryDirectStates:${currentState})`
      : 'inStock:true';

  const fallbackFilterValue = useMemo(() => {
    if (!fallbackParameters) {
      return '';
    }

    const isUS = globals.country === 'us';

    // Case-specific logic
    if (fallbackParameters.productType === 'Case') {
      if (isUS) {
        return `colour:"${fallbackParameters.colourName}" AND (threeTierStates:${currentState} OR wineryDirectStates:${currentState}) AND inStock:true`;
      }
      return `colour:"${fallbackParameters.colourName}" AND productType:"${fallbackParameters.productType}" AND inStock:true`;
    }

    // General logic for non-case products
    if (isUS) {
      return `country:"${fallbackParameters.countryName}" AND (threeTierStates:${currentState} OR wineryDirectStates:${currentState}) AND inStock:true`;
    }
    return `country:"${fallbackParameters.countryName}" AND productWebType:"${fallbackParameters.productWebType}" AND inStock:true`;
  }, [fallbackParameters, currentState]);

  useEffect(() => {
    let isMounted = true;
    const client = getRecommendClient();
    client
      .getRecommendations({
        requests: [
          {
            indexName,
            model: 'related-products',
            objectID: currentObjectID,
            maxRecommendations: 5,
            threshold: 30,
            fallbackParameters: {
              filters: fallbackFilterValue,
            },
            queryParameters: {
              filters: queryFilters,
            },
          },
        ],
      })
      .then(response => {
        if (isMounted) {
          setRecommendations(response.results[0]?.hits ?? []);
        }
      })
      .catch(() => {
        if (isMounted) {
          setRecommendations([]);
        }
      });
    return () => {
      isMounted = false;
    };
  }, [currentObjectID, indexName, queryFilters, fallbackFilterValue]);

  return (
    <div ref={targetRef} css={related} className="no-print" data-testid="related-products">
      <HeaderComponent recommendations={recommendations} />
      {renderListView({ items: recommendations, itemComponent: ItemComponent })}
    </div>
  );
};
